/**
 * Group State Service
 *
 * Signal-based state management service for FGA groups.
 * Manages the list of groups, selection state, loading indicators, and errors.
 *
 * @packageDocumentation
 */

import { Injectable, signal, computed } from '@angular/core';
import { FgaGroup } from '../models/group.model';
import { sortByName } from '../utils/sorting';

/**
 * Service responsible for managing group state using Angular signals.
 *
 * This service follows the signal-based state management pattern:
 * - Private writable signals for internal state
 * - Public readonly signals for consumers
 * - Computed signals for derived values
 * - Mutation methods for state updates
 *
 * @example
 * ```typescript
 * const groupState = inject(GroupStateService);
 *
 * // Read state (reactive)
 * const groups = groupState.groups();
 * const isLoading = groupState.loading();
 *
 * // Computed values
 * const count = groupState.groupCount();
 *
 * // Update state
 * groupState.setGroups([...]);
 * groupState.selectGroup('group:org_demo|engineering');
 * ```
 */
@Injectable()
export class GroupStateService {
  // ---------------------------------------------------------------------------
  // Private Writable Signals
  // ---------------------------------------------------------------------------

  /**
   * Internal writable signal for the list of groups
   */
  private readonly _groups = signal<FgaGroup[]>([]);

  /**
   * Internal writable signal for the selected group ID
   */
  private readonly _selectedGroupId = signal<string | null>(null);

  /**
   * Internal writable signal for loading state
   */
  private readonly _loading = signal(false);

  /**
   * Internal writable signal for error messages
   */
  private readonly _error = signal<string | null>(null);

  // ---------------------------------------------------------------------------
  // Add Group State (Private Writable Signals)
  // ---------------------------------------------------------------------------

  /**
   * Internal writable signal for add group loading state
   */
  private readonly _addingGroup = signal(false);

  /**
   * Internal writable signal for add group error
   */
  private readonly _addGroupError = signal<string | null>(null);

  // ---------------------------------------------------------------------------
  // Public Readonly Signals
  // ---------------------------------------------------------------------------

  /**
   * Readonly signal exposing the current list of groups.
   */
  readonly groups = this._groups.asReadonly();

  /**
   * Readonly signal exposing the currently selected group ID.
   */
  readonly selectedGroupId = this._selectedGroupId.asReadonly();

  /**
   * Readonly signal indicating if a loading operation is in progress.
   */
  readonly loading = this._loading.asReadonly();

  /**
   * Readonly signal exposing the current error message (null if no error).
   */
  readonly error = this._error.asReadonly();

  // ---------------------------------------------------------------------------
  // Add Group State (Public Readonly Signals)
  // ---------------------------------------------------------------------------

  /**
   * Readonly signal indicating if a group is being added.
   */
  readonly addingGroup = this._addingGroup.asReadonly();

  /**
   * Readonly signal exposing add group error message.
   */
  readonly addGroupError = this._addGroupError.asReadonly();

  // ---------------------------------------------------------------------------
  // Computed Signals
  // ---------------------------------------------------------------------------

  /**
   * Computed signal returning the total number of groups.
   */
  readonly groupCount = computed(() => this._groups().length);

  /**
   * Computed signal indicating if there are any groups.
   */
  readonly hasGroups = computed(() => this._groups().length > 0);

  /**
   * Computed signal returning the currently selected group object.
   * Returns null if no group is selected or selection doesn't match any group.
   */
  readonly selectedGroup = computed(() => {
    const id = this._selectedGroupId();
    if (!id) return null;
    return this._groups().find(g => g.id === id) ?? null;
  });

  /**
   * Computed signal returning groups sorted by name.
   */
  readonly sortedGroups = computed(() => sortByName(this._groups()));

  /**
   * Computed signal returning all group names (for duplicate checking).
   */
  readonly groupNames = computed(() => this._groups().map(g => g.name));

  // ---------------------------------------------------------------------------
  // Mutation Methods
  // ---------------------------------------------------------------------------

  /**
   * Replaces all groups with a new list.
   *
   * @param groups - Array of groups to set
   */
  setGroups(groups: FgaGroup[]): void {
    this._groups.set(sortByName(groups));
  }

  /**
   * Adds a single group to the list.
   *
   * @param group - Group to add
   */
  addGroup(group: FgaGroup): void {
    this._groups.update(current => sortByName([...current, group]));
  }

  /**
   * Updates a group by ID with partial data.
   *
   * @param groupId - ID of the group to update
   * @param updates - Partial group data to merge
   */
  updateGroup(groupId: string, updates: Partial<FgaGroup>): void {
    this._groups.update(current =>
      sortByName(current.map(g => (g.id === groupId ? { ...g, ...updates } : g)))
    );
  }

  /**
   * Removes a group by ID.
   *
   * @param groupId - ID of the group to remove
   */
  removeGroup(groupId: string): void {
    this._groups.update(current => current.filter(g => g.id !== groupId));

    // Clear selection if removed group was selected
    if (this._selectedGroupId() === groupId) {
      this._selectedGroupId.set(null);
    }
  }

  /**
   * Sets the selected group ID.
   *
   * @param groupId - ID of the group to select, or null to clear selection
   */
  selectGroup(groupId: string | null): void {
    this._selectedGroupId.set(groupId);
  }

  /**
   * Sets the loading state.
   *
   * @param loading - Whether a loading operation is in progress
   */
  setLoading(loading: boolean): void {
    this._loading.set(loading);
  }

  /**
   * Sets the error message.
   *
   * @param error - Error message, or null to clear error
   */
  setError(error: string | null): void {
    this._error.set(error);
  }

  /**
   * Clears the error state.
   */
  clearError(): void {
    this._error.set(null);
  }

  /**
   * Resets all state to initial values.
   */
  reset(): void {
    this._groups.set([]);
    this._selectedGroupId.set(null);
    this._loading.set(false);
    this._error.set(null);
  }

  /**
   * Finds a group by ID.
   *
   * @param groupId - ID to search for
   * @returns The group if found, undefined otherwise
   */
  findGroup(groupId: string): FgaGroup | undefined {
    return this._groups().find(g => g.id === groupId);
  }

  /**
   * Checks if a group with the given ID exists.
   *
   * @param groupId - ID to check
   * @returns true if the group exists
   */
  hasGroup(groupId: string): boolean {
    return this._groups().some(g => g.id === groupId);
  }

  // ---------------------------------------------------------------------------
  // Add Group Mutation Methods
  // ---------------------------------------------------------------------------

  /**
   * Sets the add group loading state.
   *
   * @param loading - Whether an add group operation is in progress
   */
  setAddingGroup(loading: boolean): void {
    this._addingGroup.set(loading);
  }

  /**
   * Sets the add group error message.
   *
   * @param error - Error message, or null to clear error
   */
  setAddGroupError(error: string | null): void {
    this._addGroupError.set(error);
  }

  /**
   * Clears the add group error state.
   */
  clearAddGroupError(): void {
    this._addGroupError.set(null);
  }
}
